import React, { useCallback, useEffect, useState } from "react";

import {
    Close as CloseIcon,
    PlayArrow as PlayArrowIcon,
} from "@mui/icons-material";
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    IconButton,
    Radio,
    RadioGroup,
    Tooltip,
    Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";

import { DEV_MODE } from "Constant/app/process";
import { ButtonVariant } from "Constant/button";
import { useParams } from "react-router-dom";

import { runBigqueryFileJob } from "helpers/Sources/bigquery/bigquerySource";
import { getSourceTrackerEvent } from "helpers/trackers/getTrackerEvent";

import StandartButton from "components/atoms/StandartButton";
import GenericTable from "components/organisms/GenericTable";
import { GenericTableColumn } from "components/organisms/GenericTable/GenericTable.types";
import SwalAlertComp from "components/SwalAlert";

import { getSourceCategory } from "../../../SourceModal.config";
import { TabProps } from "../../../SourceModal.types";

interface FileJob {
    id?: number;
    file?: string;
    total_row?: number;
    last_row?: number;
    updated_at?: string;
    last_read_time?: string;
    status_message?: string;
    manual_trigger?: boolean;
    status?: number;
    period?: string;
    started_at?: string;
    last_retried_at?: string;
    next_schedule_date?: string | null;
}

interface CRMRecord {
    day?: string;
    record_type?: string;
    source_id?: number;
    record_count_processed?: string | number;
    record_count_failed?: string | number;
    read_started_at?: string;
    read_completed_at?: string;
    status?: string;
    status_message?: string;
}

type TriggerMode = "continue" | "restart";

const MOCK_FILE_HISTORY: FileJob[] = [
    {
        id: 1001,
        file: "analytics_events_2026_08_28",
        total_row: 12540,
        last_row: 12540,
        last_read_time: "28/08/2026 09:45",
        status_message: "Completed",
        manual_trigger: true,
        next_schedule_date: "28/08/2026 10:45",
    },
    {
        id: 1002,
        file: "analytics_events_2026_08_27",
        total_row: 9842,
        last_row: 7210,
        last_read_time: "28/08/2026 08:30",
        status_message: "Processing",
        manual_trigger: false,
        next_schedule_date: "28/08/2026 11:30",
    },
    {
        id: 1003,
        file: "analytics_events_manual_import",
        total_row: 3200,
        last_row: 0,
        last_read_time: "27/08/2026 16:15",
        status_message: "Failed",
        manual_trigger: true,
        next_schedule_date: null,
    },
];

const MOCK_CRM_HISTORY: CRMRecord[] = [
    {
        day: "28/08/2026",
        record_type: "Contacts",
        source_id: 2001,
        record_count_processed: 845,
        record_count_failed: 3,
        read_completed_at: "28/08/2026 09:45",
        status_message: "Completed",
    },
    {
        day: "27/08/2026",
        record_type: "Deals",
        source_id: 2001,
        record_count_processed: 312,
        record_count_failed: 0,
        read_completed_at: "27/08/2026 18:20",
        status_message: "Completed",
    },
];

const parseHistoryDate = (dateString: string): Date | null => {
    const dmyMatch = dateString.match(
        /^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:\s+(\d{1,2}):(\d{2})(?::(\d{2}))?)?$/,
    );
    if (dmyMatch) {
        const [, day, month, year, hours = "0", minutes = "0", seconds = "0"] =
            dmyMatch;
        const parsed = new Date(
            Number(year),
            Number(month) - 1,
            Number(day),
            Number(hours),
            Number(minutes),
            Number(seconds),
        );
        return Number.isNaN(parsed.getTime()) ? null : parsed;
    }

    const fallback = new Date(dateString);
    return Number.isNaN(fallback.getTime()) ? null : fallback;
};

const formatDate = (dateString?: string): string => {
    if (!dateString) return "-";
    const date = parseHistoryDate(dateString);
    if (!date) return dateString;
    return date.toLocaleString();
};

const displayDate = (
    dateString?: string | null,
    displayAsReceived = false,
): string => {
    if (!dateString) return "-";
    return displayAsReceived ? dateString : formatDate(dateString);
};

const createStatusBadge = (status?: string) => {
    if (!status) return null;

    const statusLower = status.toLowerCase();
    let color: "success" | "error" | "primary" | "info" | "default" = "default";

    if (statusLower.includes("success") || statusLower.includes("completed")) {
        color = "success";
    } else if (
        statusLower.includes("error") ||
        statusLower.includes("failed")
    ) {
        color = "error";
    } else if (
        statusLower.includes("pending") ||
        statusLower.includes("processing")
    ) {
        color = "primary";
    }

    return (
        <Chip label={status} color={color} size="small" onClick={() => {}} />
    );
};

const mapErrorMessage = (message?: string): string => {
    if (!message) return "An error occurred.";
    const lower = message.toLowerCase();
    if (lower.includes("5 minute") || lower.includes("recently"))
        return "This file job was manually triggered recently. Please wait 5 minutes and try again.";
    if (lower.includes("invalid") || lower.includes("completed"))
        return "This file job cannot be triggered again.";
    return message;
};

const HistoryTab: React.FC<TabProps> = ({ source, language }) => {
    const theme = useTheme();
    const params = useParams<{ id: string }>();
    const [loading, setLoading] = useState(false);
    const [fileJobs, setFileJobs] = useState<FileJob[]>([]);
    const [crmData, setCrmData] = useState<CRMRecord[]>([]);
    const [pageSize, setPageSize] = useState(20);

    const [triggerDialogOpen, setTriggerDialogOpen] = useState(false);
    const [selectedJob, setSelectedJob] = useState<FileJob | null>(null);
    const [triggerMode, setTriggerMode] = useState<TriggerMode>("continue");
    const [triggering, setTriggering] = useState(false);

    const sourceCode = source?.source_type?.code;
    const category = getSourceCategory(sourceCode);
    const isBigQuery = sourceCode === "bigquery";
    const isFileSource =
        category === "file" ||
        ["ftp", "sftp", "upload", "spreadsheet"].indexOf(sourceCode || "") >= 0;
    const trackerLanguage = (language as any)?.tracker_detail;

    const fetchHistory = useCallback(async () => {
        if (DEV_MODE) {
            setFileJobs(isFileSource ? MOCK_FILE_HISTORY : []);
            setCrmData(isFileSource ? [] : MOCK_CRM_HISTORY);
            return;
        }

        if (!source?.id || !params.id) return;

        setLoading(true);
        try {
            const response = await getSourceTrackerEvent(params.id, source.id);
            if (response.status) {
                setFileJobs(response?.data?.fileJobs || []);
                setCrmData(response?.data?.zohoData || []);
            } else {
                SwalAlertComp({
                    title: language?.delete_swal_modal?.failed ?? "Failed",
                    message: response.message,
                    icon: "error",
                });
            }
        } catch (error) {
            console.error("Error fetching history:", error);
        } finally {
            setLoading(false);
        }
    }, [
        source?.id,
        params.id,
        language?.delete_swal_modal?.failed,
        isFileSource,
    ]);

    useEffect(() => {
        fetchHistory();
    }, [fetchHistory]);

    const openTriggerDialog = (job: FileJob) => {
        setSelectedJob(job);
        setTriggerMode("continue");
        setTriggerDialogOpen(true);
    };

    const closeTriggerDialog = () => {
        if (!triggering) {
            setTriggerDialogOpen(false);
            setSelectedJob(null);
        }
    };

    const handleTrigger = async () => {
        if (!selectedJob?.id || !source?.id) return;

        setTriggering(true);
        try {
            const response = await runBigqueryFileJob(source.id, {
                file_job_id: selectedJob.id,
                mode: triggerMode,
            });

            if (response.status) {
                SwalAlertComp({
                    title: language?.delete_swal_modal?.success ?? "Success",
                    message: "Job triggered successfully.",
                    icon: "success",
                    timer: 3000,
                });
                closeTriggerDialog();
                await fetchHistory();
            } else {
                SwalAlertComp({
                    title: language?.delete_swal_modal?.failed ?? "Failed",
                    message: mapErrorMessage(response.message),
                    icon: "error",
                });
            }
        } catch {
            SwalAlertComp({
                title: language?.delete_swal_modal?.failed ?? "Failed",
                message: "An error occurred.",
                icon: "error",
            });
        } finally {
            setTriggering(false);
        }
    };

    const fileJobColumns: GenericTableColumn<FileJob>[] = [
        {
            key: "file",
            header: trackerLanguage?.tracker_detail ?? "File Name",
            minWidth: 200,
            render: (item) => (
                <Box
                    sx={{
                        whiteSpace: "normal",
                        overflowWrap: "anywhere",
                        wordBreak: "break-word",
                    }}
                >
                    {item.file || "-"}
                </Box>
            ),
        },
        {
            key: "read",
            header: trackerLanguage?.read ?? "Read",
            align: "center",
            render: (item) => item.total_row ?? "-",
        },
        {
            key: "sent",
            header: trackerLanguage?.sent ?? "Sent",
            align: "center",
            render: (item) => item.last_row ?? "-",
        },
        {
            key: "lastReadTime",
            header: trackerLanguage?.last_read_time ?? "Last Read Time",
            align: "center",
            minWidth: 160,
            render: (item) =>
                displayDate(item.last_read_time || item.updated_at, isBigQuery),
        },
        ...(isBigQuery
            ? [
                  {
                      key: "nextScheduleDate",
                      header: "Schedule Date & Time",
                      align: "center" as const,
                      minWidth: 180,
                      render: (item: FileJob) =>
                          displayDate(item.next_schedule_date, true),
                  },
              ]
            : []),
        {
            key: "status",
            header: trackerLanguage?.status ?? "Status",
            align: "center",
            render: (item) => createStatusBadge(item.status_message),
        },
        ...(isBigQuery
            ? [
                  {
                      key: "actions",
                      header: "",
                      align: "center" as const,
                      fixedWidth: 80,
                      hideable: false,
                      cardActions: true,
                      render: (item: FileJob) => (
                          <Tooltip title="Run manually">
                              <StandartButton
                                  variant={ButtonVariant.ICON_BUTTON}
                                  rounded
                                  disabled={!item.manual_trigger || triggering}
                                  onClick={() => openTriggerDialog(item)}
                              >
                                  <PlayArrowIcon fontSize="small" />
                              </StandartButton>
                          </Tooltip>
                      ),
                  },
              ]
            : []),
    ];

    const crmColumns: GenericTableColumn<CRMRecord>[] = [
        {
            key: "recordType",
            header: trackerLanguage?.record_type ?? "Record Type",
            minWidth: 200,
            render: (item) => item.record_type || "-",
        },
        {
            key: "read",
            header: trackerLanguage?.read ?? "Read",
            align: "center",
            render: (item) => item.record_count_processed ?? "-",
        },
        {
            key: "failed",
            header: language?.delete_swal_modal?.failed ?? "Failed",
            align: "center",
            render: (item) => item.record_count_failed ?? "-",
        },
        {
            key: "lastReadTime",
            header: trackerLanguage?.last_read_time ?? "Last Read Time",
            align: "center",
            minWidth: 160,
            render: (item) => displayDate(item.read_completed_at),
        },
        {
            key: "status",
            header: trackerLanguage?.status ?? "Status",
            align: "center",
            render: (item) => createStatusBadge(item.status_message),
        },
    ];

    const emptyState = (
        <Box sx={{ textAlign: "center", py: 4 }}>
            <Typography variant="body1" color="text.secondary">
                No updated records were found in the last 7 days.
            </Typography>
        </Box>
    );

    if (loading) {
        return (
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    py: 4,
                }}
            >
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                {language?.buttons?.history ??
                    (isFileSource ? "Processing History" : "Sync History")}
            </Typography>
            {isFileSource ? (
                <GenericTable
                    columns={fileJobColumns}
                    rows={fileJobs}
                    getRowKey={(item, index) => item.id ?? index}
                    emptyState={emptyState}
                    minWidth={isBigQuery ? 980 : 760}
                    pageSize={pageSize}
                    onPageSizeChange={setPageSize}
                />
            ) : (
                <GenericTable
                    columns={crmColumns}
                    rows={crmData}
                    getRowKey={(item, index) =>
                        `${item.source_id ?? "source"}-${item.day ?? index}-${index}`
                    }
                    emptyState={emptyState}
                    minWidth={760}
                    pageSize={pageSize}
                    onPageSizeChange={setPageSize}
                />
            )}

            <Dialog
                open={triggerDialogOpen}
                onClose={closeTriggerDialog}
                maxWidth="xs"
                fullWidth
                PaperProps={{ sx: { borderRadius: 2 } }}
            >
                <DialogTitle
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        pb: 1,
                        borderBottom: `1px solid ${theme.palette.divider}`,
                    }}
                >
                    <Typography variant="h6" component="div">
                        Start BigQuery Read
                    </Typography>
                    <IconButton
                        onClick={closeTriggerDialog}
                        disabled={triggering}
                        size="small"
                        sx={{ color: theme.palette.text.secondary }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ pt: 3, mt: 3 }}>
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 2 }}
                    >
                        How would you like to start the read operation for this
                        file job?
                    </Typography>
                    <FormControl>
                        <RadioGroup
                            value={triggerMode}
                            onChange={(e) =>
                                setTriggerMode(e.target.value as TriggerMode)
                            }
                        >
                            <FormControlLabel
                                value="continue"
                                control={<Radio />}
                                label="Continue from where it left off"
                                disabled={triggering}
                            />
                            <FormControlLabel
                                value="restart"
                                control={<Radio />}
                                label="Read from the beginning"
                                disabled={triggering}
                            />
                        </RadioGroup>
                    </FormControl>
                </DialogContent>
                <DialogActions sx={{ p: 2, gap: 1 }}>
                    <Button
                        onClick={closeTriggerDialog}
                        disabled={triggering}
                        variant="outlined"
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleTrigger}
                        disabled={triggering}
                        variant="contained"
                    >
                        {triggering ? (
                            <CircularProgress size={20} color="inherit" />
                        ) : (
                            "Run"
                        )}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default HistoryTab;
